import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import type { GetClassStudentAnswersQuery } from '../../present/activity-answers.graphql.generated';
import { Student, StudentProfile } from '../classroom.constants';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import type { RichTextAnswerFragment } from '../../present/activity-answers.graphql.generated';
import {
  ACTIVITIES_ANSWER_DRAW_TYPE,
  ACTIVITIES_ANSWER_TEXT,
} from 'global/constants';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { isRichtextValueEmpty } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { hasDrawAnswer } from 'approot/activity-builder/components/shared/logic/has-draw-answer';
import type { Pluck } from 'lib/gql/pluck';

type StudentResponse = StudentProfile & {
  drawAnswer: DrawAnswerFragment | null;
  textAnswer: RichTextAnswerFragment | null;
  drawAnswerUuid: string | null;
  updatedAt: Date | null;
};

export function getStudentDiscussionAnswers({
  studentsInClass,
  studentAnswers,
}: {
  studentsInClass: Student[];
  studentAnswers: Pluck<
    GetClassStudentAnswersQuery,
    'getClassStudentsAnswers'
  >[];
}): StudentResponse[] {
  const groupedAnswers = groupBy(studentAnswers, 'studentId');

  const answers = studentsInClass.map(student => {
    const allAnswers = groupedAnswers[student.id] || [];
    const drawAnswer = allAnswers.find(
      ans => ans.answerType === ACTIVITIES_ANSWER_DRAW_TYPE
    );
    const textAnswer = allAnswers.find(
      ans => ans.answerType === ACTIVITIES_ANSWER_TEXT
    );

    return {
      studentId: student.id,
      studentName: getStudentDisplayName(student),
      studentEmoji: student.emeji,
      drawAnswerUuid: drawAnswer?.uuid || null,
      drawAnswer: (drawAnswer?.answer as DrawAnswerFragment) || null,
      textAnswer: (textAnswer?.answer as RichTextAnswerFragment) || null,
      updatedAt: drawAnswer?.updatedAt ? new Date(drawAnswer.updatedAt) : null,
    };
  });

  const sortedAnswers = sortBy(answers, answer =>
    answer.studentName.toLowerCase()
  );

  const studentsWithAnswers = sortedAnswers.filter(
    student =>
      hasDrawAnswer(student.drawAnswer) ||
      !isRichtextValueEmpty(student.textAnswer?.richtext)
  );

  const studentsWithoutAnswers = sortedAnswers.filter(
    student =>
      !hasDrawAnswer(student.drawAnswer) &&
      isRichtextValueEmpty(student.textAnswer?.richtext)
  );

  return [...studentsWithAnswers, ...studentsWithoutAnswers];
}
